import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { createPortal } from 'react-dom';

function AddToListDropdown({ courseId, onListsUpdate, isMobile }) {
  const [user, setUser] = useState(null);
  const [playedCourses, setPlayedCourses] = useState([]);
  const [wishlistCourses, setWishlistCourses] = useState([]);
  const [customLists, setCustomLists] = useState({});
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownCoords, setDropdownCoords] = useState({ top: 0, left: 0 });
  const [error, setError] = useState('');
  const buttonRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        await loadUserData(firebaseUser.uid);
      } else {
        setPlayedCourses([]);
        setWishlistCourses([]);
        setCustomLists({});
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Add scroll and resize event listeners to update dropdown position
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;
        const dropdownHeight = 400;
        const dropdownWidth = 300;
        
        const spaceAbove = rect.top;
        const spaceBelow = windowHeight - rect.bottom;
        const spaceOnRight = windowWidth - rect.left;
        
        let leftPosition = rect.left;
        if (spaceOnRight < dropdownWidth) {
          leftPosition = windowWidth - dropdownWidth - 10;
        }
        if (leftPosition < 10) {
          leftPosition = 10;
        }
        
        if (spaceAbove > spaceBelow) {
          setDropdownCoords({ 
            top: rect.top - 250,
            left: leftPosition
          });
        } else {
          setDropdownCoords({ 
            top: rect.bottom + 4,
            left: leftPosition
          });
        }
      }
    };

    const handleResize = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  const loadUserData = async (uid) => {
    try {
      const userRef = doc(db, "users", uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const data = userSnap.data();
        setPlayedCourses(data.playedCourses || []);
        setWishlistCourses(data.wishlistCourses || []);
        setCustomLists(data.customLists || {});
      }
    } catch (err) {
      console.error('Error loading user data:', err);
      setError('Failed to load user data');
    }
  };

  const handleAddToList = async (listType, listId = null) => {
    if (!user) return;

    try {
      const userRef = doc(db, "users", user.uid);
      
      if (listType === 'played') {
        const isPlayed = playedCourses.includes(courseId);
        if (isPlayed) {
          await updateDoc(userRef, { playedCourses: arrayRemove(courseId) });
          setPlayedCourses(prev => prev.filter(id => id !== courseId));
        } else {
          await updateDoc(userRef, { playedCourses: arrayUnion(courseId) });
          setPlayedCourses(prev => [...prev, courseId]);
        }
      } else if (listType === 'wishlist') {
        const isWishlisted = wishlistCourses.includes(courseId);
        if (isWishlisted) {
          await updateDoc(userRef, { wishlistCourses: arrayRemove(courseId) });
          setWishlistCourses(prev => prev.filter(id => id !== courseId));
        } else {
          await updateDoc(userRef, { wishlistCourses: arrayUnion(courseId) });
          setWishlistCourses(prev => [...prev, courseId]);
        }
      } else if (listType === 'custom' && listId) {
        const list = customLists[listId];
        const isInList = list.courses.includes(courseId);
        
        const updatedList = {
          ...list,
          courses: isInList 
            ? list.courses.filter(id => id !== courseId)
            : [...list.courses, courseId]
        };

        const updatedLists = {
          ...customLists,
          [listId]: updatedList
        };

        await updateDoc(userRef, { customLists: updatedLists });
        setCustomLists(updatedLists);
        onListsUpdate && onListsUpdate(updatedLists);
      }
    } catch (err) {
      console.error('Error updating list:', err);
      setError('Failed to update list');
    }
  };

  const isPlayed = playedCourses.includes(courseId);
  const isWishlisted = wishlistCourses.includes(courseId);
  const customListStatus = Object.entries(customLists)
    .map(([id, list]) => ({
      id,
      name: list.name,
      isInList: list.courses.includes(courseId),
      courseCount: list.courses.length
    }))
    .sort((a, b) => b.courseCount - a.courseCount); // Sort by course count (most courses first)

  if (loading) {
    return <div style={{ color: '#666', fontSize: '14px' }}>Loading...</div>;
  }

  if (!user) {
    return null;
  }

  const handleToggleDropdown = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      const dropdownHeight = 400; // Approximate height of dropdown
      const dropdownWidth = 300; // Approximate width of dropdown
      
      // Check if there's enough space above the button
      const spaceAbove = rect.top;
      const spaceBelow = windowHeight - rect.bottom;
      
      // Check horizontal space
      const spaceOnRight = windowWidth - rect.left;
      const spaceOnLeft = rect.left;
      
      // Calculate left position to prevent cutoff
      let leftPosition = rect.left;
      if (spaceOnRight < dropdownWidth) {
        // If not enough space on right, align to right edge with some margin
        leftPosition = windowWidth - dropdownWidth - 10;
      }
      
      // Ensure minimum left position
      if (leftPosition < 10) {
        leftPosition = 10;
      }
      
      // If there's more space above than below, position above
      if (spaceAbove > spaceBelow) {
        setDropdownCoords({ 
          top: rect.top - 250, // Position slightly higher above the button
          left: leftPosition
        });
      } else {
        setDropdownCoords({ 
          top: rect.bottom + 4, // 4px margin below
          left: leftPosition
        });
      }
    }
    setIsOpen(!isOpen);
  };



    return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={buttonRef}
        onClick={handleToggleDropdown}
                 style={{
           background: '#1D665D',
           color: 'white',
           border: 'none',
           padding: isMobile ? '8px' : '8px 16px',
           borderRadius: '8px',
           cursor: 'pointer',
           fontSize: '14px',
           fontWeight: '600',
           display: 'flex',
           alignItems: 'center',
           gap: '6px',
           whiteSpace: 'nowrap'
         }}
      >
                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
           <path d="M12 5v14M5 12h14"/>
         </svg>
         {!isMobile && 'Add to List'}
      </button>

                     {/* Portal for dropdown to render outside any constraining containers */}
        {isOpen && createPortal(
                     <div style={{
             position: 'fixed',
             top: dropdownCoords.top,
             left: dropdownCoords.left,
             background: 'white',
             border: '1px solid #ddd',
             borderRadius: '8px',
             boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
             zIndex: 1000,
             minWidth: isMobile ? '250px' : '300px',
             maxWidth: isMobile ? '280px' : '350px',
             maxHeight: '400px',
             overflow: 'hidden',
             display: 'flex',
             flexDirection: 'column'
           }}>
           {/* Scrollable content container */}
           <div style={{ 
             overflowY: 'auto', 
             maxHeight: '350px',
             flex: 1
           }}>
             {/* Default Lists */}
             <div style={{ padding: '8px 0' }}>
                            <div
                  onClick={() => handleAddToList('played')}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: isPlayed ? '#1D665D' : '#333',
                    fontWeight: isPlayed ? '600' : '400',
                    background: isPlayed ? '#f0f8f7' : 'transparent',
                    wordBreak: 'break-word'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isPlayed}
                    readOnly
                    style={{ accentColor: '#1D665D', flexShrink: 0 }}
                  />
                                  <span style={{ flex: 1, paddingRight: '8px' }}>Played</span>
                </div>
                <div
                  onClick={() => handleAddToList('wishlist')}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: isWishlisted ? '#1D665D' : '#333',
                    fontWeight: isWishlisted ? '600' : '400',
                    background: isWishlisted ? '#f0f8f7' : 'transparent',
                    wordBreak: 'break-word'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isWishlisted}
                    readOnly
                    style={{ accentColor: '#1D665D', flexShrink: 0 }}
                  />
                                  <span style={{ flex: 1, paddingRight: '8px' }}>Bucket List</span>
                </div>
             </div>

             {/* Custom Lists */}
             {customListStatus.length > 0 && (
               <>
                 <div style={{
                   borderTop: '1px solid #eee',
                   margin: '4px 0'
                 }} />
                 <div style={{ padding: '8px 0' }}>
                                    {customListStatus.map(({ id, name, isInList }) => (
                      <div
                        key={id}
                        onClick={() => handleAddToList('custom', id)}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          color: isInList ? '#1D665D' : '#333',
                          fontWeight: isInList ? '600' : '400',
                          background: isInList ? '#f0f8f7' : 'transparent',
                          wordBreak: 'break-word'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isInList}
                          readOnly
                          style={{ accentColor: '#1D665D', flexShrink: 0 }}
                        />
                                              <span style={{ flex: 1, paddingRight: '8px' }}>{name}</span>
                      </div>
                    ))}
                 </div>
               </>
             )}
           </div>

           {/* Create New List Option - always visible at bottom */}
           <div style={{
             borderTop: '1px solid #eee',
             margin: '4px 0',
             flexShrink: 0
           }} />
                        <div
              onClick={() => {
                setIsOpen(false);
                // This will be handled by the parent component
                window.location.href = '/profile?tab=lists';
              }}
                            style={{
                 padding: '8px 12px',
                 cursor: 'pointer',
                 color: '#1D665D',
                 fontWeight: '600',
                 fontSize: '14px',
                 wordBreak: 'break-word',
                 paddingRight: '20px',
                 flexShrink: 0
               }}
            >
              + Create New List
            </div>
                                   </div>,
          document.body
        )}

        {/* Click outside to close */}
        {isOpen && createPortal(
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
            onClick={() => setIsOpen(false)}
          />,
          document.body
        )}
      </div>
   );
}

export default AddToListDropdown; 